import { Helmet } from 'react-helmet-async';
import { seoConfig } from '@/config/seo';

interface WebSiteSchemaProps {
  searchAction?: {
    target: string;
    queryInput: string;
  };
  potentialActions?: Array<{
    type: string;
    name: string;
    url: string;
  }>;
}

export const WebSiteSchema: React.FC<WebSiteSchemaProps> = ({
  searchAction = {
    target: `${seoConfig.site.url}/search?q={search_term_string}`,
    queryInput: 'required name=search_term_string',
  },
  potentialActions = [
    {
      type: 'ReadAction',
      name: 'Read Blog Posts',
      url: `${seoConfig.site.url}/blog`,
    },
    {
      type: 'ViewAction',
      name: 'View Services',
      url: `${seoConfig.site.url}/services`,
    },
    {
      type: 'ContactAction',
      name: 'Contact Us',
      url: `${seoConfig.site.url}/contact`,
    },
  ],
}) => {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${seoConfig.site.url}#website`,
    name: seoConfig.site.name,
    alternateName: 'FortifyTek Solutions',
    url: seoConfig.site.url,
    description: seoConfig.site.description,
    publisher: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
    },
    potentialAction: [
      {
        '@type': 'SearchAction',
        target: searchAction.target,
        'query-input': searchAction.queryInput,
      },
      ...potentialActions.map(action => ({
        '@type': action.type,
        name: action.name,
        url: action.url,
      })),
    ],
    mainEntity: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
    },
    about: {
      '@type': 'Thing',
      name: 'Enterprise Technology Solutions',
      description: 'AI solutions, NetSuite development, Shopify Plus, and business intelligence services',
    },
    keywords: seoConfig.defaultMeta.keywords.join(', '),
    inLanguage: 'en-US',
    copyrightYear: new Date().getFullYear(),
    copyrightHolder: {
      '@type': 'Organization',
      '@id': `${seoConfig.site.url}#organization`,
    },
    license: `${seoConfig.site.url}/terms`,
    accessibilityAPI: 'ARIA',
    accessibilityControl: ['fullKeyboardControl', 'fullMouseControl', 'fullTouchControl'],
    accessibilityFeature: [
      'alternativeText',
      'captions',
      'describedAt',
      'highContrastDisplay',
      'largePrint',
      'resizeText',
    ],
    accessibilityHazard: 'none',
    accessibilitySummary: 'This website is designed to be accessible to users with disabilities.',
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};